// AIStudioView.tsx - Complete Implementation
// Properly integrates with your existing ClaudeService and project architecture

import React, { useState, useEffect } from 'react';
import { Principal } from '@dfinity/principal';
import {
    Target,
    Layers,
    Edit3,
    Settings,
    Users,
    Wand2,
    Plus,
    Eye,
    Sparkles,
    MessageSquare,
    Play,
    Code,
    Database,
    Zap,
    FileText,
    Coins
} from 'lucide-react';
import ProjectCodeAnalyzer, { CodeAnalysisResult, ProjectFile } from './ProjectCodeAnalyzer';
import FeaturesAndFunctionsPanel from "./FeaturesAndFunctionsPanel";
import DesignStylePanel from "./DesignStylePanel";
import DataLogicPanel from "./DataLogicPanel";
import UserExperiencePanel from "./UserExperiencePanel";
import AIStudioChat from './AIStudioChat';
import DeFiLabPanel from "./DeFiLabPanel";


interface AIStudioViewProps {
    project: any;
    files: ProjectFile[];
    systemCanisters: any;
    userCanisters: any[];
    userPrincipal: Principal | null;
    onError: (error: string) => void;
    onSuccess: (message: string) => void;
    onProjectUpdate?: () => Promise<void>;
    onFileSystemChange?: () => void;
    // ADD these missing props:
    activeFile: string;
    fileContent: string;
    onUpdateContent: (content: string) => void;
}

interface AISuggestion {
    id: string;
    type: 'feature' | 'enhancement' | 'optimization' | 'fix';
    title: string;
    description: string;
    impact: 'low' | 'medium' | 'high';
    effort: 'low' | 'medium' | 'high';
    category: string;
    estimatedTime: string;
    businessValue?: string;
}

interface ProjectAnalysis {
    complexity: 'simple' | 'medium' | 'complex';
    projectType: string;
    backendFiles: number;
    frontendFiles: number;
    totalLines: number;
    languages: string[];
    features: string[];
    issues: string[];
    suggestions: AISuggestion[];
}

const AIStudioView: React.FC<AIStudioViewProps> = ({
                                                       project,
                                                       files,
                                                       systemCanisters,
                                                       userCanisters,
                                                       userPrincipal,
                                                       onError,
                                                       onSuccess,
                                                       onProjectUpdate,
                                                       onFileSystemChange,
                                                       activeFile,
                                                       fileContent,
                                                       onUpdateContent
                                                   }) => {
    const [activeSection, setActiveSection] = useState('chat');
    const [isAIThinking, setIsAIThinking] = useState(false);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [projectAnalysis, setProjectAnalysis] = useState<ProjectAnalysis | null>(null);
    const [suggestions, setSuggestions] = useState<AISuggestion[]>([]);
    const [codeAnalysis, setCodeAnalysis] = useState<CodeAnalysisResult | null>(null);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [modalContent, setModalContent] = useState<{title: string, content: string}>({title: '', content: ''});
    const [showFileSelector, setShowFileSelector] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
    const [pendingSuggestion, setPendingSuggestion] = useState<AISuggestion | null>(null);
    const [applyProgress, setApplyProgress] = useState<{message: string, progress: number} | null>(null);

    // Initialize analyzer and smart file selection
    const analyzer = new ProjectCodeAnalyzer();

    // Smart file selection on component load
    useEffect(() => {
        if (files.length > 0) {
            // Auto-select key files (main files, not dependencies)
            const keyFiles = files.filter(file => {
                const name = file.name.toLowerCase();
                return (
                    !name.includes('node_modules') &&
                    !name.includes('.test.') &&
                    !name.includes('.spec.') &&
                    !name.includes('package-lock.json') &&
                    !name.includes('.git') &&
                    (name.endsWith('.mo') || // All Motoko files
                        name.includes('app.') || // Main app files
                        name.includes('index.') || // Entry points
                        name.includes('main.') || // Main files
                        name.includes('component') || // Key components
                        (name.endsWith('.tsx') && file.content && file.content.length < 2000)) // Smaller React files
                );
            }).slice(0, 8); // Limit to 8 files max

            setSelectedFiles(new Set(keyFiles.map(f => f.name)));
        }
    }, [files]);

    // Analyze project when component loads
    useEffect(() => {
        if (project && files.length > 0) {
            console.log(`🎯 AI Studio: Project loaded - ${project.name} with ${files.length} files`);
            // analyzeProject();
        }
    }, [project, files]);

    /**
     * Get API key from platform (same as your ChatSidebar)
     */
    const getApiKeyFromPlatform = async (): Promise<string> => {
        if (!systemCanisters.platformActor) {
            throw new Error('Platform canister not initialized');
        }

        try {
            const result = await systemCanisters.platformActor.getClaudeApiKey();
            if ('ok' in result) {
                return result.ok;
            } else {
                throw new Error(result.err || 'Failed to retrieve Claude API key');
            }
        } catch (error) {
            console.error('Error fetching API key from platform:', error);
            throw new Error(`Claude API not configured: ${error instanceof Error ? error.message : String(error)}`);
        }
    };

    /**
     * Build comprehensive context (with selected files only)
     */
    const buildProjectContext = () => {
        const fileContents: { [fileName: string]: string } = {};

        // Only include selected files
        files.forEach(file => {
            if (selectedFiles.has(file.name) && file.content && file.content.trim().length > 0) {
                fileContents[file.name] = file.content;
            }
        });

        const projectInfo = {
            id: project?.id || '',
            name: project?.name || 'Unknown Project',
            type: 'ICP Application'
        };

        const totalChars = Object.values(fileContents).reduce((sum, content) => sum + content.length, 0);
        console.log(`🔧 Context: ${Object.keys(fileContents).length} selected files, ${totalChars.toLocaleString()} characters`);

        return {
            fileContents,
            projectInfo,
            comprehensiveAIContext: '' // You can integrate with useIDEStore aiRules here
        };
    };

    /**
     * Generate AI suggestions specific to a feature
     */
    const generateFeatureSpecificSuggestions = async (feature: string) => {
        setIsAIThinking(true);
        try {
            console.log(`🎯 AI Studio: Generating suggestions for feature "${feature}"`);

            const apiKey = await getApiKeyFromPlatform();
            const context = buildProjectContext();

            const featurePrompt = `Analyze the "${feature}" functionality in this ICP project and provide 2-3 specific improvement suggestions.

Focus specifically on enhancing the "${feature}" feature:
- How to improve its implementation
- Additional capabilities to add
- Performance or UX optimizations
- Integration with other project features

Provide structured suggestions in JSON format:
{
    "suggestions": [
        {
            "id": "feature-${feature.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}",
            "type": "enhancement|feature|optimization",
            "title": "Specific improvement for ${feature}",
            "description": "Detailed description focusing on ${feature}",
            "impact": "low|medium|high",
            "effort": "low|medium|high", 
            "category": "${feature}",
            "estimatedTime": "1-2 hours",
            "businessValue": "Why this ${feature} improvement matters"
        }
    ]
}`;

            const response = await fetch('https://ai.coinnation.io/api/claude/analyze', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    apiKey,
                    prompt: featurePrompt,
                    context,
                    analysisType: 'feature-suggestions',
                    preferredModel: 'sonnet-4'
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Feature analysis failed: ${response.status}`);
            }

            const data = await response.json();
            const newSuggestions = parseAISuggestions(data.content);

            const uniqueSuggestions = newSuggestions.filter(newSug =>
                !suggestions.some(existingSug => existingSug.id === newSug.id)
            );

            setSuggestions(prev => [...prev, ...uniqueSuggestions]);
            onSuccess(`Generated ${uniqueSuggestions.length} suggestions for ${feature}!`);

        } catch (error) {
            console.error(`🚨 AI Studio: Error generating ${feature} suggestions:`, error);
            onError(`Failed to generate ${feature} suggestions: ${error instanceof Error ? error.message : String(error)}`);
        } finally {
            setIsAIThinking(false);
        }
    };

    /**
     * Generate AI suggestions using /api/claude/analyze endpoint
     */
    const generateAISuggestions = async () => {
        setIsAIThinking(true);
        try {
            console.log(`🤖 AI Studio: Requesting suggestions for ${files.length} files`);

            const apiKey = await getApiKeyFromPlatform();
            const context = buildProjectContext();

            const analysisPrompt = `Analyze this ICP project and provide 3-5 specific, actionable improvement suggestions.

Project Overview:
- Name: ${project?.name || 'ICP Application'}
- Files: ${Object.keys(context.fileContents).length}
- Languages: ${getProjectLanguages(context.fileContents).join(', ')}

Focus on:
1. ICP-specific optimizations and best practices
2. Code quality and maintainability improvements  
3. User experience enhancements
4. Performance and security considerations
5. Missing features that would add significant value

Provide structured suggestions in JSON format:
{
    "suggestions": [
        {
            "id": "unique-id-1",
            "type": "enhancement|feature|optimization|fix",
            "title": "Clear, actionable title",
            "description": "Detailed description of the improvement",
            "impact": "low|medium|high",
            "effort": "low|medium|high", 
            "category": "Performance|Security|UX|Code Quality|Features",
            "estimatedTime": "1-2 hours",
            "businessValue": "Why this matters to users/business"
        }
    ]
}`;

            console.log(`🚀 Calling /api/claude/analyze with ${analysisPrompt.length} char prompt`);

            const response = await fetch('https://ai.coinnation.io/api/claude/analyze', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    apiKey,
                    prompt: analysisPrompt,
                    context,
                    analysisType: 'suggestions',
                    preferredModel: 'sonnet-4'
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Analysis failed: ${response.status}`);
            }

            const data = await response.json();
            console.log(`✅ Analysis response: ${data.content.length} chars`);

            const newSuggestions = parseAISuggestions(data.content);

            // Filter out any suggestions that might have duplicate IDs
            const uniqueSuggestions = newSuggestions.filter(newSug =>
                !suggestions.some(existingSug => existingSug.id === newSug.id)
            );

            setSuggestions(prev => [...prev, ...uniqueSuggestions]);
            onSuccess(`Generated ${uniqueSuggestions.length} new AI suggestions!`);

        } catch (error) {
            console.error('🚨 AI Studio: Error generating suggestions:', error);
            onError(`AI suggestion generation failed: ${error instanceof Error ? error.message : String(error)}`);
        } finally {
            setIsAIThinking(false);
        }
    };

    /**
     * Apply AI suggestion using /api/claude/analyze (non-streaming for efficiency)
     */
    const applySuggestionWithAI = async (suggestion: AISuggestion) => {
        setIsAIThinking(true);
        setApplyProgress({ message: 'Preparing request...', progress: 10 });

        try {
            console.log(`🛠️ AI Studio: Applying suggestion "${suggestion.title}"`);
            console.log(`🔧 Debug: systemCanisters available:`, !!systemCanisters.userActor);
            console.log(`🔧 Debug: userPrincipal available:`, !!userPrincipal);
            console.log(`🔧 Debug: project available:`, !!project?.id);

            const apiKey = await getApiKeyFromPlatform();
            console.log(`🔧 Debug: API key obtained:`, !!apiKey);

            const context = buildProjectContext();
            console.log(`🔧 Debug: Context built - ${Object.keys(context.fileContents).length} files`);

            setApplyProgress({ message: 'Sending to AI...', progress: 20 });

            const implementationPrompt = `Implement this specific improvement in the ICP project:

**Suggestion**: ${suggestion.title}
**Description**: ${suggestion.description}
**Type**: ${suggestion.type}
**Expected Impact**: ${suggestion.impact}

Requirements:
1. Provide COMPLETE file content for any modified files (not snippets)
2. Maintain all existing functionality while adding the improvement
3. Follow ICP and React best practices
4. Include proper error handling and type safety
5. Add appropriate comments explaining changes

Current project structure: ${Object.keys(context.fileContents).length} files
Languages: ${getProjectLanguages(context.fileContents).join(', ')}

For each file that needs changes, provide complete updated content with proper file path comments.`;

            console.log(`🚀 Calling non-streaming API for efficiency...`);

            // Use the faster non-streaming endpoint
            const response = await fetch('https://ai.coinnation.io/api/claude/chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    apiKey,
                    messages: [{ role: 'user', content: implementationPrompt }],
                    context,
                    needsFileExtraction: true,  // ← Need files for implementation
                    preferredModel: 'sonnet-4',
                    tokenBudget: 150000
                })
            });

            console.log(`📡 Response status: ${response.status}`);

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`❌ API Error Response:`, errorText);
                throw new Error(`Implementation failed: ${response.status} - ${errorText}`);
            }

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.body?.error || `Implementation failed: ${response.status}`);
            }

            setApplyProgress({ message: 'Processing AI response...', progress: 60 });

            const data = await response.json();

            // ✅ Files are now properly extracted by backend!
            const extractedFiles = data.body.files || {};

            console.log(`✅ Backend extracted ${Object.keys(extractedFiles).length} files`);
            Object.entries(extractedFiles).forEach(([fileName, content]) => {
                // console.log(`   - ${fileName}: ${content.length} chars`);
            });

            setApplyProgress({ message: 'Applying file changes...', progress: 80 });

            if (Object.keys(extractedFiles).length > 0) {
                await applyFileChangesToProject(extractedFiles);
                setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
                onSuccess(`Successfully applied: ${suggestion.title}`);

                if (onFileSystemChange) {
                    onFileSystemChange();
                }
            } else {
                onError('AI response did not contain any file modifications');
            }

            setApplyProgress({ message: 'Complete!', progress: 100 });
            setTimeout(() => setApplyProgress(null), 2000);

        } catch (error) {
            console.error('🚨 AI Studio: Error applying suggestion:', error);
            console.error('🚨 Error stack:', error instanceof Error ? error.stack : 'No stack trace');
            onError(`Failed to apply suggestion: ${error instanceof Error ? error.message : String(error)}`);
        } finally {
            setIsAIThinking(false);
            setApplyProgress(null);
        }
    };

    /**
     * Process streaming implementation response with better progress tracking
     */
    const processStreamingImplementation = async (response: Response, suggestion: AISuggestion) => {
        const reader = response.body?.getReader();
        if (!reader) {
            throw new Error('No reader available from streaming response');
        }

        const decoder = new TextDecoder();
        let buffer = '';
        let accumulatedContent = '';
        let extractedFiles: { [key: string]: string } = {};

        // Set timeout for streaming response
        const timeoutId = setTimeout(() => {
            reader.cancel();
            throw new Error('Streaming response timed out after 60 seconds');
        }, 60000); // 60 second timeout

        try {
            setApplyProgress({ message: 'Connecting to AI service...', progress: 10 });
            console.log(`🔄 Progress set: Connecting (10%)`);

            while (true) {
                const { done, value } = await reader.read();
                console.log(`📖 Stream read: done=${done}, value length=${value?.length || 0}`);

                if (done) break;

                const chunk = decoder.decode(value, { stream: true });
                console.log(`📝 Decoded chunk: "${chunk.substring(0, 100)}..."`);
                buffer += chunk;

                const lines = buffer.split('\n');
                buffer = lines.pop() || '';
                console.log(`📋 Processing ${lines.length} lines`);

                for (const line of lines) {
                    if (line.trim() === '') continue;
                    console.log(`📄 Processing line: "${line.substring(0, 50)}..."`);

                    if (line.startsWith('data: ')) {
                        const dataStr = line.slice(6);
                        console.log(`📊 Data string: "${dataStr.substring(0, 100)}..."`);

                        try {
                            const eventData = JSON.parse(dataStr);
                            console.log(`✅ Parsed event:`, eventData);

                            switch (eventData.type) {
                                case 'connected':
                                    console.log('🔗 Connected to implementation streaming');
                                    setApplyProgress({ message: 'Connected to AI service', progress: 20 });
                                    console.log(`🔄 Progress set: Connected (20%)`);
                                    break;

                                case 'progress':
                                    console.log(`🔄 Implementation: ${eventData.progress}% - ${eventData.message}`);
                                    setApplyProgress({
                                        message: eventData.message || 'Processing...',
                                        progress: Math.min(eventData.progress || 30, 80)
                                    });
                                    break;

                                case 'content_delta':
                                    if (eventData.content) {
                                        accumulatedContent += eventData.content;
                                        setApplyProgress({
                                            message: 'Receiving AI response...',
                                            progress: Math.min(50 + (accumulatedContent.length / 100), 75)
                                        });
                                    }
                                    break;

                                case 'complete':
                                    console.log('✅ Implementation streaming complete');
                                    setApplyProgress({ message: 'Processing files...', progress: 85 });

                                    if (eventData.files) {
                                        extractedFiles = eventData.files;
                                    } else {
                                        // Try to extract files from accumulated content
                                        extractedFiles = detectCompleteFiles(accumulatedContent);
                                    }

                                    setApplyProgress({ message: 'Applying changes...', progress: 90 });

                                    // Apply file changes if any were generated
                                    if (Object.keys(extractedFiles).length > 0) {
                                        await applyFileChangesToProject(extractedFiles);
                                    } else {
                                        console.warn('⚠️ No files detected in AI response');
                                        onError('AI response did not contain any file modifications');
                                        return;
                                    }

                                    setApplyProgress({ message: 'Complete!', progress: 100 });

                                    // Remove applied suggestion
                                    setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
                                    onSuccess(`Successfully applied: ${suggestion.title}`);

                                    if (onFileSystemChange) {
                                        onFileSystemChange();
                                    }

                                    // Clear progress after a moment
                                    setTimeout(() => setApplyProgress(null), 2000);
                                    return;

                                case 'error':
                                    console.error('🚨 Implementation streaming error:', eventData.message);
                                    throw new Error(eventData.message || 'Implementation streaming error');
                            }
                        } catch (parseError) {
                            console.log(`⚠️ Skipping invalid JSON chunk: ${dataStr.substring(0, 50)}...`);
                            // Continue processing, this might be partial JSON
                        }
                    }
                }
            }

            // If we reach here without completing, it means stream ended unexpectedly
            if (Object.keys(extractedFiles).length === 0) {
                console.warn('⚠️ Stream ended without completion event');
                extractedFiles = detectCompleteFiles(accumulatedContent);

                if (Object.keys(extractedFiles).length > 0) {
                    setApplyProgress({ message: 'Applying detected changes...', progress: 90 });
                    await applyFileChangesToProject(extractedFiles);
                    setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
                    onSuccess(`Successfully applied: ${suggestion.title}`);
                    if (onFileSystemChange) onFileSystemChange();
                } else {
                    throw new Error('No file modifications detected in AI response');
                }
            }

        } finally {
            clearTimeout(timeoutId);
            reader.releaseLock();
            setApplyProgress(null);
        }
    };

    /**
     * Apply generated file changes to the project (same pattern as your ChatSidebar)
     */
    const applyFileChangesToProject = async (fileChanges: { [fileName: string]: string }) => {
        if (!systemCanisters.userActor || !userPrincipal || !project?.id) {
            throw new Error('Required dependencies not available for file operations');
        }

        console.log(`🔧 Applying ${Object.keys(fileChanges).length} backend-extracted files`);

        let successCount = 0;
        let errorCount = 0;

        for (const [filePath, fileContent] of Object.entries(fileChanges)) {
            try {
                let cleanFilePath = filePath.trim();

                // ✅ FIX: Remove project name prefix if it exists
                if (cleanFilePath.startsWith(project.name + '/')) {
                    cleanFilePath = cleanFilePath.substring(project.name.length + 1);
                    console.log(`📁 Stripped project prefix: ${filePath} → ${cleanFilePath}`);
                }

                // Extract filename and directory
                let fileName: string;
                let directoryPath: string;

                if (cleanFilePath.includes('/')) {
                    const pathParts = cleanFilePath.split('/');
                    fileName = pathParts.pop() || cleanFilePath;
                    directoryPath = `${project.name}/${pathParts.join('/')}`; // Add project name back
                } else {
                    fileName = cleanFilePath;
                    directoryPath = project.name; // Default to project root
                }

                // Determine file type from extension (keep existing logic)
                const extension = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
                let fileType = 'text/plain';
                let languageType = 'text';

                switch (extension) {
                    case '.mo':
                        fileType = 'text/x-motoko';
                        languageType = 'motoko';
                        break;
                    case '.tsx':
                    case '.ts':
                        fileType = 'text/typescript';
                        languageType = 'typescript';
                        break;
                    case '.jsx':
                    case '.js':
                        fileType = 'text/javascript';
                        languageType = 'javascript';
                        break;
                    case '.css':
                        fileType = 'text/css';
                        languageType = 'css';
                        break;
                    case '.json':
                        fileType = 'application/json';
                        languageType = 'json';
                        break;
                    case '.md':
                        fileType = 'text/markdown';
                        languageType = 'markdown';
                        break;
                }

                console.log(`📁 Final path: ${directoryPath}/${fileName} (${fileContent.length} chars)`);

                // Try to create the file first
                const createResult = await systemCanisters.userActor.createCodeArtifact(
                    userPrincipal,
                    project.id,
                    fileName,
                    { Text: fileContent },
                    fileType,
                    languageType,
                    directoryPath,
                    []
                );

                if ('ok' in createResult) {
                    successCount++;
                    console.log(`✅ Created: ${directoryPath}/${fileName}`);
                } else {
                    // File exists, try to update it
                    const updateResult = await systemCanisters.userActor.updateCodeArtifact(
                        userPrincipal,
                        project.id,
                        fileName,
                        { Text: fileContent },
                        directoryPath,
                        []
                    );

                    if ('ok' in updateResult) {
                        successCount++;
                        console.log(`🔄 Updated: ${directoryPath}/${fileName}`);
                    } else {
                        errorCount++;
                        console.error(`❌ Failed to update: ${directoryPath}/${fileName}`, updateResult.err);
                    }
                }

            } catch (error) {
                errorCount++;
                console.error(`💥 Error processing ${filePath}:`, error);
            }
        }

        if (successCount > 0) {
            onSuccess(`Applied ${successCount} file changes successfully!`);
        }

        if (errorCount > 0) {
            onError(`Failed to apply ${errorCount} file changes.`);
        }
    };

    /**
     * Parse AI suggestions from response
     */
    const parseAISuggestions = (aiResponse: string): AISuggestion[] => {
        try {
            // Try to extract JSON from the response
            const jsonMatch = aiResponse.match(/```json\s*([\s\S]*?)\s*```/);
            if (jsonMatch) {
                const parsed = JSON.parse(jsonMatch[1]);
                if (parsed.suggestions && Array.isArray(parsed.suggestions)) {
                    return parsed.suggestions.map((s: any, index: number) => ({
                        id: s.id || `suggestion-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
                        type: s.type || 'enhancement',
                        title: s.title || 'AI Suggestion',
                        description: s.description || '',
                        impact: s.impact || 'medium',
                        effort: s.effort || 'medium',
                        category: s.category || 'General',
                        estimatedTime: s.estimatedTime || '1-2 hours',
                        businessValue: s.businessValue || ''
                    }));
                }
            }

            // Fallback: parse suggestions from text format
            return parseTextSuggestions(aiResponse);

        } catch (error) {
            console.error('🚨 Error parsing AI suggestions:', error);
            return [];
        }
    };

    /**
     * Fallback text parsing for suggestions
     */
    const parseTextSuggestions = (text: string): AISuggestion[] => {
        const suggestions: AISuggestion[] = [];

        // Look for numbered suggestions or bullet points
        const patterns = [
            /(\d+)\.\s*\*\*(.*?)\*\*([\s\S]*?)(?=\d+\.\s*\*\*|$)/g,
            /[-*]\s*\*\*(.*?)\*\*([\s\S]*?)(?=[-*]\s*\*\*|$)/g
        ];

        let suggestionCounter = 0;
        for (const pattern of patterns) {
            let match;
            while ((match = pattern.exec(text)) !== null) {
                const title = match[2] || match[1];
                const description = match[3] || match[2];

                suggestions.push({
                    id: `text-suggestion-${Date.now()}-${suggestionCounter++}-${Math.random().toString(36).substr(2, 9)}`,
                    type: 'enhancement',
                    title: title.trim(),
                    description: description.trim().substring(0, 200) + '...',
                    impact: 'medium',
                    effort: 'medium',
                    category: 'AI Generated',
                    estimatedTime: '1-2 hours'
                });
            }
        }

        return suggestions;
    };

    /**
     * Detect complete files from AI response content
     */
    const detectCompleteFiles = (content: string): { [key: string]: string } => {
        const files: { [key: string]: string } = {};

        // Enhanced pattern matching for file extraction
        const patterns = [
            {
                regex: /```(?:tsx?|typescript|javascript|jsx)\s*\n(?:\/\/\s*)?Complete file:\s*([^\n]+\.(?:tsx?|jsx?|js))[^\n]*\n([\s\S]*?)```/gi,
                type: 'typescript'
            },
            {
                regex: /```css\s*\n\/\*\s*Complete file:\s*([^\n*]+\.css)[^\n*]*\*\/\s*\n([\s\S]*?)```/gi,
                type: 'css'
            },
            {
                regex: /```motoko\s*\n(?:\/\/\s*)?Complete file:\s*([^\n]+\.mo)[^\n]*\n([\s\S]*?)```/gi,
                type: 'motoko'
            }
        ];

        patterns.forEach(({ regex }) => {
            let match;
            while ((match = regex.exec(content)) !== null) {
                let filename = match[1].trim();
                const fileContent = match[2].trim();

                if (fileContent && fileContent.length > 50) {
                    files[filename] = fileContent;
                }
            }
        });

        return files;
    };

    /**
     * Get icon for feature type
     */
    const getFeatureIcon = (feature: string) => {
        const iconMap: { [key: string]: string } = {
            'Interactive UI': '🖱️',
            'Forms': '📝',
            'API Integration': '🔌',
            'State Management': '📊',
            'Routing': '🧭',
            'Styling': '🎨',
            'Authentication': '🔐',
            'Database': '🗄️',
            'File Upload': '📁',
            'Real-time': '⚡',
            'Search': '🔍',
            'Chat': '💬'
        };
        return iconMap[feature] || '⚙️';
    };

    /**
     * Get description for feature
     */
    const getFeatureDescription = (feature: string) => {
        const descriptions: { [key: string]: string } = {
            'Interactive UI': 'User interface with clickable elements, buttons, and dynamic interactions',
            'Forms': 'Input forms for data collection and user interaction',
            'API Integration': 'Connections to external services and data sources',
            'State Management': 'Application state handling and data flow management',
            'Routing': 'Navigation between different pages and views',
            'Styling': 'Visual design, CSS, and user interface styling',
            'Authentication': 'User login, registration, and access control',
            'Database': 'Data storage and retrieval functionality',
            'File Upload': 'File handling and upload capabilities',
            'Real-time': 'Live updates and real-time data synchronization',
            'Search': 'Search functionality and filtering capabilities',
            'Chat': 'Messaging and communication features'
        };
        return descriptions[feature] || 'A detected feature in your application';
    };

    /**
     * Get stats for feature
     */
    const getFeatureStats = (feature: string) => {
        // Count files that might be related to this feature
        const relatedFiles = files.filter(file => {
            const content = file.content?.toLowerCase() || '';
            const fileName = file.name.toLowerCase();

            switch (feature) {
                case 'Interactive UI':
                    return content.includes('onclick') || content.includes('button') || content.includes('click');
                case 'Forms':
                    return content.includes('form') || content.includes('input') || content.includes('submit');
                case 'API Integration':
                    return content.includes('fetch') || content.includes('api') || content.includes('axios');
                case 'State Management':
                    return content.includes('usestate') || content.includes('state') || content.includes('redux');
                case 'Routing':
                    return content.includes('route') || content.includes('router') || content.includes('navigate');
                case 'Styling':
                    return fileName.includes('.css') || content.includes('style') || content.includes('className');
                default:
                    return false;
            }
        });

        return `${relatedFiles.length} related files found`;
    };

    /**
     * Show detailed feature information
     */
    const showFeatureDetails = (feature: string) => {
        const relatedFiles = files.filter(file => {
            const content = file.content?.toLowerCase() || '';
            const fileName = file.name.toLowerCase();

            switch (feature) {
                case 'Interactive UI':
                    return content.includes('onclick') || content.includes('button') || content.includes('click');
                case 'Forms':
                    return content.includes('form') || content.includes('input') || content.includes('submit');
                case 'API Integration':
                    return content.includes('fetch') || content.includes('api') || content.includes('axios');
                case 'State Management':
                    return content.includes('usestate') || content.includes('state') || content.includes('redux');
                case 'Routing':
                    return content.includes('route') || content.includes('router') || content.includes('navigate');
                case 'Styling':
                    return fileName.includes('.css') || content.includes('style') || content.includes('className');
                default:
                    return false;
            }
        });

        const details = `${getFeatureDescription(feature)}

Related Files (${relatedFiles.length}):
${relatedFiles.map(f => `• ${f.name}`).join('\n') || 'No specific files detected'}

Suggestions:
• Click "Enhance" to get AI-powered improvements
• Use file selector to include related files in analysis
• Check the Overview tab for general suggestions

This feature was detected by analyzing your code for relevant keywords and patterns.`;

        setModalContent({
            title: `${getFeatureIcon(feature)} ${feature} Details`,
            content: details
        });
        setShowInfoModal(true);
    };

    /**
     * Helper: Get project languages from file contents
     */
    const getProjectLanguages = (fileContents: { [key: string]: string }): string[] => {
        const languages = new Set<string>();

        Object.keys(fileContents).forEach(fileName => {
            const extension = fileName.split('.').pop()?.toLowerCase();
            switch (extension) {
                case 'mo': languages.add('Motoko'); break;
                case 'tsx': case 'ts': languages.add('TypeScript'); break;
                case 'jsx': case 'js': languages.add('JavaScript'); break;
                case 'css': languages.add('CSS'); break;
                default: break;
            }
        });

        return Array.from(languages);
    };

    /**
     * Local project analysis (triggers AI suggestions automatically)
     */
    const analyzeProject = async () => {
        setIsAnalyzing(true);
        try {
            // Run code analysis first
            const codeResult = await analyzer.analyzeCurrentCode(files);
            setCodeAnalysis(codeResult);

            // Basic project analysis
            const backendFiles = files.filter(f => f.name.endsWith('.mo')).length;
            const frontendFiles = files.filter(f => f.name.endsWith('.tsx') || f.name.endsWith('.ts') || f.name.endsWith('.jsx') || f.name.endsWith('.js')).length;
            const totalLines = files.reduce((sum, file) => sum + (file.content?.split('\n').length || 0), 0);

            const languages = getProjectLanguages(
                files.reduce((acc, file) => {
                    acc[file.name] = file.content || '';
                    return acc;
                }, {} as { [key: string]: string })
            );

            // Detect features by analyzing code content
            const allContent = files.map(f => f.content || '').join('\n').toLowerCase();
            const features: string[] = [];

            if (allContent.includes('button') || allContent.includes('onclick')) features.push('Interactive UI');
            if (allContent.includes('form') || allContent.includes('input')) features.push('Forms');
            if (allContent.includes('fetch') || allContent.includes('api')) features.push('API Integration');
            if (allContent.includes('usestate') || allContent.includes('state')) features.push('State Management');
            if (allContent.includes('router') || allContent.includes('route')) features.push('Routing');
            if (allContent.includes('css') || allContent.includes('style')) features.push('Styling');

            // Determine complexity
            let complexity: 'simple' | 'medium' | 'complex' = 'simple';
            if (totalLines > 1000 || languages.length > 3 || features.length > 4) {
                complexity = 'complex';
            } else if (totalLines > 500 || languages.length > 2 || features.length > 2) {
                complexity = 'medium';
            }

            // Detect issues
            const issues: string[] = [];
            if (!allContent.includes('error') && !allContent.includes('try')) {
                issues.push('Missing error handling');
            }
            if (frontendFiles > 0 && !allContent.includes('typescript')) {
                issues.push('Consider adding TypeScript for better type safety');
            }
            if (!allContent.includes('test')) {
                issues.push('No tests detected');
            }

            const analysis: ProjectAnalysis = {
                complexity,
                projectType: backendFiles > 0 && frontendFiles > 0 ? 'Full-stack' :
                    backendFiles > 0 ? 'Backend' : 'Frontend',
                backendFiles,
                frontendFiles,
                totalLines,
                languages,
                features,
                issues,
                suggestions: []
            };

            setProjectAnalysis(analysis);
            onSuccess('Project analysis complete!');

            // Automatically generate AI suggestions after analysis
            // if (files.length > 0) {
            //     console.log('🎯 Automatically generating AI suggestions...');
            //     setTimeout(() => {
            //         generateAISuggestions();
            //     }, 500);
            // }

        } catch (error) {
            onError(`Analysis failed: ${error instanceof Error ? error.message : String(error)}`);
        } finally {
            setIsAnalyzing(false);
        }
    };

    // Loading state
    if (isAnalyzing) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                padding: '40px',
                textAlign: 'center'
            }}>
                <div style={{
                    width: '60px',
                    height: '60px',
                    border: '4px solid #f3e5d8',
                    borderRadius: '50%',
                    borderTop: '4px solid #8B4513',
                    animation: 'spin 1s linear infinite',
                    marginBottom: '20px'
                }}></div>
                <h3 style={{ color: '#5C4033', marginBottom: '10px' }}>Analyzing Your Project</h3>
                <p style={{ color: '#8B4513' }}>AI is examining your code structure, features, and opportunities...</p>
            </div>
        );
    }

    // if (!projectAnalysis) {
    //     return (
    //         <div style={{
    //             display: 'flex',
    //             flexDirection: 'column',
    //             alignItems: 'center',
    //             justifyContent: 'center',
    //             height: '100%',
    //             padding: '40px',
    //             textAlign: 'center'
    //         }}>
    //             <Sparkles size={48} style={{ color: '#8B4513', marginBottom: '20px' }} />
    //             <h3 style={{ color: '#5C4033', marginBottom: '10px' }}>Welcome to AI Studio</h3>
    //             <p style={{ color: '#8B4513', marginBottom: '20px' }}>
    //                 Analyze your project structure and code patterns locally. AI features available in chat.
    //             </p>
    //             <div style={{
    //                 backgroundColor: '#fef3c7',
    //                 padding: '12px 16px',
    //                 borderRadius: '8px',
    //                 marginBottom: '20px',
    //                 border: '1px solid #fde68a',
    //                 maxWidth: '400px'
    //             }}>
    //                 <div style={{ fontSize: '14px', color: '#92400e', textAlign: 'left' }}>
    //                     💡 <strong>What this does:</strong>
    //                     <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
    //                         <li>Analyzes code structure locally</li>
    //                         <li>Detects features and complexity</li>
    //                         <li>No automatic AI calls</li>
    //                         <li>Use chat for AI assistance</li>
    //                     </ul>
    //                 </div>
    //             </div>
    //             <button
    //                 onClick={analyzeProject}
    //                 disabled={isAnalyzing}
    //                 style={{
    //                     padding: '12px 24px',
    //                     backgroundColor: isAnalyzing ? '#9B5523' : '#5C4033',
    //                     color: '#FFF5EE',
    //                     border: 'none',
    //                     borderRadius: '6px',
    //                     cursor: isAnalyzing ? 'not-allowed' : 'pointer',
    //                     fontWeight: '500',
    //                     display: 'flex',
    //                     alignItems: 'center',
    //                     gap: '8px'
    //                 }}
    //             >
    //                 {isAnalyzing ? (
    //                     <>
    //                         <div style={{
    //                             width: '16px',
    //                             height: '16px',
    //                             border: '2px solid rgba(255,255,255,0.3)',
    //                             borderTopColor: '#FFF5EE',
    //                             borderRadius: '50%',
    //                             animation: 'spin 1s linear infinite'
    //                         }} />
    //                         Analyzing Locally...
    //                     </>
    //                 ) : (
    //                     <>
    //                         <Wand2 size={16} />
    //                         Analyze Project (Local Only)
    //                     </>
    //                 )}
    //             </button>
    //
    //             {/* Additional info */}
    //             <div style={{
    //                 marginTop: '24px',
    //                 fontSize: '12px',
    //                 color: '#8B4513',
    //                 maxWidth: '500px',
    //                 lineHeight: '1.4'
    //             }}>
    //                 This will perform local code analysis without making AI API calls.
    //                 For AI-powered suggestions and code generation, use the chat interface above.
    //             </div>
    //         </div>
    //     );
    // }

    // Main interface
    return (
        <div style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
            {/* Navigation Tabs */}
            <div style={{backgroundColor: '#f3e5d8', borderBottom: '1px solid #8B4513'}}>
                <div style={{display: 'flex'}}>
                    {[
                        {id: 'chat', label: 'AI Chat', icon: MessageSquare},
                        // {id: 'overview', label: 'Project Overview', icon: Target},
                        // {id: 'features', label: 'Features & Functions', icon: Layers},
                        // {id: 'defi', label: 'DeFi Lab', icon: Coins},
                        // {id: 'design', label: 'Design & Style', icon: Edit3},
                        // {id: 'data', label: 'Data & Logic', icon: Settings},
                        // {id: 'users', label: 'User Experience', icon: Users}
                    ].map((tab, index) => {
                        const IconComponent = tab.icon;
                        return (
                            <React.Fragment key={tab.id}>
                                <button
                                    onClick={() => setActiveSection(tab.id)}
                                    style={{
                                        padding: '12px 24px',
                                        backgroundColor: activeSection === tab.id ? '#fdf6e3' : '#f3e5d8',
                                        color: activeSection === tab.id ? '#5C4033' : '#8B4513',
                                        border: 'none',
                                        borderBottom: activeSection === tab.id ? '2px solid #5C4033' : '2px solid transparent',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontSize: '14px',
                                        fontWeight: activeSection === tab.id ? '600' : '400'
                                    }}
                                >
                                    <IconComponent size={16}/>
                                    {tab.label}
                                </button>
                                {index < 3 && ( // Don't add divider after last tab
                                    <div style={{
                                        width: '2px',
                                        backgroundColor: '#5C4033',
                                        opacity: 0.6,
                                        alignSelf: 'stretch'
                                    }} />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>

            {/* Main Content Area */}
            <div style={{
                flex: 1,
                overflow: 'auto',
                padding: activeSection === 'chat' ? '0' : '24px'
            }}>
                {/*{activeSection === 'overview' && projectAnalysis && (*/}
                {/*    <div style={{maxWidth: '1320px', margin: '0 auto'}}>*/}
                {/*        /!* Project Summary Card *!/*/}
                {/*        <div style={{*/}
                {/*            backgroundColor: '#fff',*/}
                {/*            borderRadius: '12px',*/}
                {/*            padding: '24px',*/}
                {/*            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',*/}
                {/*            marginBottom: '24px',*/}
                {/*            border: '1px solid #e5d8c8'*/}
                {/*        }}>*/}
                {/*            <div style={{*/}
                {/*                display: 'flex',*/}
                {/*                justifyContent: 'space-between',*/}
                {/*                alignItems: 'start',*/}
                {/*                marginBottom: '20px'*/}
                {/*            }}>*/}
                {/*                <div>*/}
                {/*                    <h2 style={{fontSize: '24px', fontWeight: 'bold', color: '#5C4033', margin: 0}}>*/}
                {/*                        {project?.name || 'Your Project'}*/}
                {/*                    </h2>*/}
                {/*                    <p style={{color: '#8B4513', margin: '8px 0 0 0', fontSize: '16px'}}>*/}
                {/*                        {projectAnalysis.projectType} • {projectAnalysis.complexity} complexity*/}
                {/*                    </p>*/}
                {/*                </div>*/}
                {/*                <span style={{*/}
                {/*                    padding: '6px 12px',*/}
                {/*                    backgroundColor: projectAnalysis.complexity === 'simple' ? '#e2f1e8' :*/}
                {/*                        projectAnalysis.complexity === 'medium' ? '#e3f2fd' : '#ffebee',*/}
                {/*                    color: projectAnalysis.complexity === 'simple' ? '#2d6a4f' :*/}
                {/*                        projectAnalysis.complexity === 'medium' ? '#1976d2' : '#c62828',*/}
                {/*                    borderRadius: '20px',*/}
                {/*                    fontSize: '14px',*/}
                {/*                    fontWeight: '500'*/}
                {/*                }}>*/}
                {/*                    {projectAnalysis.complexity.toUpperCase()}*/}
                {/*                </span>*/}
                {/*            </div>*/}

                {/*            /!* Stats Grid - MADE CLICKABLE *!/*/}
                {/*            <div style={{*/}
                {/*                display: 'grid',*/}
                {/*                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',*/}
                {/*                gap: '16px',*/}
                {/*                marginBottom: '20px'*/}
                {/*            }}>*/}
                {/*                <button*/}
                {/*                    onClick={() => {*/}
                {/*                        const fileDetails = `Total Files: ${files.length}\nLines of Code: ${projectAnalysis.totalLines.toLocaleString()}\n\nFile Breakdown:\n${files.map(f => `• ${f.name} (${f.content?.split('\n').length || 0} lines)`).join('\n')}`;*/}
                {/*                        setModalContent({*/}
                {/*                            title: 'Code Files Overview',*/}
                {/*                            content: fileDetails*/}
                {/*                        });*/}
                {/*                        setShowInfoModal(true);*/}
                {/*                    }}*/}
                {/*                    style={{*/}
                {/*                        padding: '16px',*/}
                {/*                        backgroundColor: '#f0f7ff',*/}
                {/*                        borderRadius: '8px',*/}
                {/*                        border: '1px solid #c3dafe',*/}
                {/*                        cursor: 'pointer',*/}
                {/*                        textAlign: 'left',*/}
                {/*                        transition: 'all 0.2s ease'*/}
                {/*                    }}*/}
                {/*                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dbeafe'}*/}
                {/*                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f0f7ff'}*/}
                {/*                >*/}
                {/*                    <div style={{*/}
                {/*                        display: 'flex',*/}
                {/*                        alignItems: 'center',*/}
                {/*                        gap: '8px',*/}
                {/*                        marginBottom: '8px'*/}
                {/*                    }}>*/}
                {/*                        <Code size={20} style={{color: '#1976d2'}}/>*/}
                {/*                        <span style={{fontWeight: '600', color: '#1976d2'}}>Code Files</span>*/}
                {/*                    </div>*/}
                {/*                    <div style={{fontSize: '20px', fontWeight: 'bold', color: '#5C4033'}}>*/}
                {/*                        {files.length}*/}
                {/*                    </div>*/}
                {/*                    <div style={{fontSize: '12px', color: '#8B4513', marginTop: '4px'}}>*/}
                {/*                        {projectAnalysis.totalLines.toLocaleString()} lines of code*/}
                {/*                    </div>*/}
                {/*                </button>*/}

                {/*                <button*/}
                {/*                    onClick={() => {*/}
                {/*                        const backendFiles = files.filter(f => f.name.endsWith('.mo'));*/}
                {/*                        const backendDetails = `Backend Files: ${backendFiles.length}\n\nMotoko Canisters:\n${backendFiles.map(f => `• ${f.name} (${f.content?.split('\n').length || 0} lines)`).join('\n') || 'No backend files found'}`;*/}
                {/*                        setModalContent({*/}
                {/*                            title: 'Backend Files Overview',*/}
                {/*                            content: backendDetails*/}
                {/*                        });*/}
                {/*                        setShowInfoModal(true);*/}
                {/*                    }}*/}
                {/*                    style={{*/}
                {/*                        padding: '16px',*/}
                {/*                        backgroundColor: '#f0fdf4',*/}
                {/*                        borderRadius: '8px',*/}
                {/*                        border: '1px solid #bbf7d0',*/}
                {/*                        cursor: 'pointer',*/}
                {/*                        textAlign: 'left',*/}
                {/*                        transition: 'all 0.2s ease'*/}
                {/*                    }}*/}
                {/*                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dcfce7'}*/}
                {/*                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f0fdf4'}*/}
                {/*                >*/}
                {/*                    <div style={{*/}
                {/*                        display: 'flex',*/}
                {/*                        alignItems: 'center',*/}
                {/*                        gap: '8px',*/}
                {/*                        marginBottom: '8px'*/}
                {/*                    }}>*/}
                {/*                        <Database size={20} style={{color: '#16a34a'}}/>*/}
                {/*                        <span style={{fontWeight: '600', color: '#16a34a'}}>Backend Files</span>*/}
                {/*                    </div>*/}
                {/*                    <div style={{fontSize: '20px', fontWeight: 'bold', color: '#5C4033'}}>*/}
                {/*                        {projectAnalysis.backendFiles}*/}
                {/*                    </div>*/}
                {/*                    <div style={{fontSize: '12px', color: '#8B4513', marginTop: '4px'}}>*/}
                {/*                        Motoko canisters*/}
                {/*                    </div>*/}
                {/*                </button>*/}

                {/*                <button*/}
                {/*                    onClick={() => {*/}
                {/*                        const frontendFiles = files.filter(f => f.name.endsWith('.tsx') || f.name.endsWith('.ts') || f.name.endsWith('.jsx') || f.name.endsWith('.js'));*/}
                {/*                        const frontendDetails = `Frontend Files: ${frontendFiles.length}\n\nReact Components:\n${frontendFiles.map(f => `• ${f.name} (${f.content?.split('\n').length || 0} lines)`).join('\n') || 'No frontend files found'}`;*/}
                {/*                        setModalContent({*/}
                {/*                            title: 'Frontend Files Overview',*/}
                {/*                            content: frontendDetails*/}
                {/*                        });*/}
                {/*                        setShowInfoModal(true);*/}
                {/*                    }}*/}
                {/*                    style={{*/}
                {/*                        padding: '16px',*/}
                {/*                        backgroundColor: '#fef3c7',*/}
                {/*                        borderRadius: '8px',*/}
                {/*                        border: '1px solid #fde68a',*/}
                {/*                        cursor: 'pointer',*/}
                {/*                        textAlign: 'left',*/}
                {/*                        transition: 'all 0.2s ease'*/}
                {/*                    }}*/}
                {/*                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fde68a'}*/}
                {/*                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fef3c7'}*/}
                {/*                >*/}
                {/*                    <div style={{*/}
                {/*                        display: 'flex',*/}
                {/*                        alignItems: 'center',*/}
                {/*                        gap: '8px',*/}
                {/*                        marginBottom: '8px'*/}
                {/*                    }}>*/}
                {/*                        <Eye size={20} style={{color: '#d97706'}}/>*/}
                {/*                        <span style={{fontWeight: '600', color: '#d97706'}}>Frontend Files</span>*/}
                {/*                    </div>*/}
                {/*                    <div style={{fontSize: '20px', fontWeight: 'bold', color: '#5C4033'}}>*/}
                {/*                        {projectAnalysis.frontendFiles}*/}
                {/*                    </div>*/}
                {/*                    <div style={{fontSize: '12px', color: '#8B4513', marginTop: '4px'}}>*/}
                {/*                        React components*/}
                {/*                    </div>*/}
                {/*                </button>*/}

                {/*                <button*/}
                {/*                    onClick={() => {*/}
                {/*                        const featureDetails = `Detected Features: ${projectAnalysis.features.length}\n\nCapabilities Found:\n${projectAnalysis.features.map(f => `• ${f}`).join('\n')}\n\nClick individual feature tags below to get AI suggestions for specific features.`;*/}
                {/*                        setModalContent({*/}
                {/*                            title: 'Project Features Overview',*/}
                {/*                            content: featureDetails*/}
                {/*                        });*/}
                {/*                        setShowInfoModal(true);*/}
                {/*                    }}*/}
                {/*                    style={{*/}
                {/*                        padding: '16px',*/}
                {/*                        backgroundColor: '#fdf2f8',*/}
                {/*                        borderRadius: '8px',*/}
                {/*                        border: '1px solid #f9a8d4',*/}
                {/*                        cursor: 'pointer',*/}
                {/*                        textAlign: 'left',*/}
                {/*                        transition: 'all 0.2s ease'*/}
                {/*                    }}*/}
                {/*                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fce7f3'}*/}
                {/*                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fdf2f8'}*/}
                {/*                >*/}
                {/*                    <div style={{*/}
                {/*                        display: 'flex',*/}
                {/*                        alignItems: 'center',*/}
                {/*                        gap: '8px',*/}
                {/*                        marginBottom: '8px'*/}
                {/*                    }}>*/}
                {/*                        <Sparkles size={20} style={{color: '#ec4899'}}/>*/}
                {/*                        <span style={{fontWeight: '600', color: '#ec4899'}}>Features</span>*/}
                {/*                    </div>*/}
                {/*                    <div style={{fontSize: '20px', fontWeight: 'bold', color: '#5C4033'}}>*/}
                {/*                        {projectAnalysis.features.length}*/}
                {/*                    </div>*/}
                {/*                    <div style={{fontSize: '12px', color: '#8B4513', marginTop: '4px'}}>*/}
                {/*                        Detected capabilities*/}
                {/*                    </div>*/}
                {/*                </button>*/}
                {/*            </div>*/}

                {/*            /!* Languages and Features *!/*/}
                {/*            <div style={{marginBottom: '20px'}}>*/}
                {/*                <h4 style={{*/}
                {/*                    color: '#5C4033',*/}
                {/*                    marginBottom: '12px',*/}
                {/*                    fontSize: '16px',*/}
                {/*                    fontWeight: '600'*/}
                {/*                }}>*/}
                {/*                    Technologies Used*/}
                {/*                </h4>*/}
                {/*                <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px'}}>*/}
                {/*                    {projectAnalysis.languages.map((lang, index) => (*/}
                {/*                        <button*/}
                {/*                            key={index}*/}
                {/*                            onClick={() => {*/}
                {/*                                const langFiles = files.filter(f => {*/}
                {/*                                    const ext = f.name.split('.').pop()?.toLowerCase();*/}
                {/*                                    return (*/}
                {/*                                        (lang === 'Motoko' && ext === 'mo') ||*/}
                {/*                                        (lang === 'TypeScript' && (ext === 'tsx' || ext === 'ts')) ||*/}
                {/*                                        (lang === 'JavaScript' && (ext === 'jsx' || ext === 'js')) ||*/}
                {/*                                        (lang === 'CSS' && ext === 'css')*/}
                {/*                                    );*/}
                {/*                                });*/}
                {/*                                const langDetails = `${lang} Files: ${langFiles.length}\n\nFiles Found:\n${langFiles.map(f => `• ${f.name} (${f.content?.split('\n').length || 0} lines)`).join('\n') || `No ${lang} files found`}`;*/}
                {/*                                setModalContent({*/}
                {/*                                    title: `${lang} Technology Overview`,*/}
                {/*                                    content: langDetails*/}
                {/*                                });*/}
                {/*                                setShowInfoModal(true);*/}
                {/*                            }}*/}
                {/*                            style={{*/}
                {/*                                padding: '4px 12px',*/}
                {/*                                backgroundColor: '#f3e5d8',*/}
                {/*                                color: '#5C4033',*/}
                {/*                                borderRadius: '16px',*/}
                {/*                                fontSize: '12px',*/}
                {/*                                fontWeight: '500',*/}
                {/*                                border: '1px solid #8B4513',*/}
                {/*                                cursor: 'pointer',*/}
                {/*                                transition: 'all 0.2s ease'*/}
                {/*                            }}*/}
                {/*                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5d8c8'}*/}
                {/*                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f3e5d8'}*/}
                {/*                        >*/}
                {/*                            {lang}*/}
                {/*                        </button>*/}
                {/*                    ))}*/}
                {/*                </div>*/}

                {/*                <h4 style={{*/}
                {/*                    color: '#5C4033',*/}
                {/*                    marginBottom: '12px',*/}
                {/*                    fontSize: '16px',*/}
                {/*                    fontWeight: '600'*/}
                {/*                }}>*/}
                {/*                    Detected Features*/}
                {/*                </h4>*/}
                {/*                <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px'}}>*/}
                {/*                    {projectAnalysis.features.map((feature, index) => (*/}
                {/*                        <button*/}
                {/*                            key={index}*/}
                {/*                            onClick={() => {*/}
                {/*                                // Generate AI suggestions specific to this feature*/}
                {/*                                generateFeatureSpecificSuggestions(feature);*/}
                {/*                            }}*/}
                {/*                            style={{*/}
                {/*                                padding: '4px 12px',*/}
                {/*                                backgroundColor: '#e2f1e8',*/}
                {/*                                color: '#2d6a4f',*/}
                {/*                                borderRadius: '16px',*/}
                {/*                                fontSize: '12px',*/}
                {/*                                fontWeight: '500',*/}
                {/*                                border: '1px solid #2d6a4f',*/}
                {/*                                cursor: 'pointer',*/}
                {/*                                transition: 'all 0.2s ease'*/}
                {/*                            }}*/}
                {/*                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d1fae5'}*/}
                {/*                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#e2f1e8'}*/}
                {/*                        >*/}
                {/*                            {feature}*/}
                {/*                        </button>*/}
                {/*                    ))}*/}
                {/*                </div>*/}
                {/*            </div>*/}
                {/*        </div>*/}

                {/*        <div style={{*/}
                {/*            backgroundColor: '#fff',*/}
                {/*            borderRadius: '12px',*/}
                {/*            padding: '24px',*/}
                {/*            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',*/}
                {/*            border: '1px solid #e5d8c8',*/}
                {/*            marginBottom: '16px'*/}
                {/*        }}>*/}
                {/*            <div style={{*/}
                {/*                display: 'flex',*/}
                {/*                justifyContent: 'space-between',*/}
                {/*                alignItems: 'center',*/}
                {/*                marginBottom: '16px'*/}
                {/*            }}>*/}
                {/*                <h3 style={{color: '#5C4033', fontSize: '16px', fontWeight: '600', margin: 0}}>*/}
                {/*                    📁 Selected Files for AI Analysis*/}
                {/*                </h3>*/}
                {/*                <button*/}
                {/*                    onClick={() => setShowFileSelector(true)}*/}
                {/*                    style={{*/}
                {/*                        padding: '6px 12px',*/}
                {/*                        backgroundColor: '#8B4513',*/}
                {/*                        color: '#FFF5EE',*/}
                {/*                        border: 'none',*/}
                {/*                        borderRadius: '4px',*/}
                {/*                        cursor: 'pointer',*/}
                {/*                        fontSize: '12px',*/}
                {/*                        fontWeight: '500'*/}
                {/*                    }}*/}
                {/*                >*/}
                {/*                    Manage Files ({selectedFiles.size})*/}
                {/*                </button>*/}
                {/*            </div>*/}
                {/*            <div style={{fontSize: '14px', color: '#8B4513'}}>*/}
                {/*                <div style={{marginBottom: '8px'}}>*/}
                {/*                    <strong>Selected:</strong> {selectedFiles.size} of {files.length} files*/}
                {/*                </div>*/}
                {/*                <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px'}}>*/}
                {/*                    {Array.from(selectedFiles).slice(0, 6).map(fileName => (*/}
                {/*                        <span key={fileName} style={{*/}
                {/*                            padding: '2px 8px',*/}
                {/*                            backgroundColor: '#f3e5d8',*/}
                {/*                            color: '#5C4033',*/}
                {/*                            borderRadius: '12px',*/}
                {/*                            fontSize: '11px',*/}
                {/*                            fontWeight: '500'*/}
                {/*                        }}>*/}
                {/*                            {fileName}*/}
                {/*                        </span>*/}
                {/*                    ))}*/}
                {/*                    {selectedFiles.size > 6 && (*/}
                {/*                        <span style={{*/}
                {/*                            padding: '2px 8px',*/}
                {/*                            backgroundColor: '#e5d8c8',*/}
                {/*                            color: '#5C4033',*/}
                {/*                            borderRadius: '12px',*/}
                {/*                            fontSize: '11px',*/}
                {/*                            fontWeight: '500'*/}
                {/*                        }}>*/}
                {/*                            +{selectedFiles.size - 6} more*/}
                {/*                        </span>*/}
                {/*                    )}*/}
                {/*                </div>*/}
                {/*                {selectedFiles.size > 10 && (*/}
                {/*                    <div style={{marginTop: '8px', fontSize: '12px', color: '#d97706'}}>*/}
                {/*                        ⚠️ {selectedFiles.size} files may be too many for Claude. Consider selecting*/}
                {/*                        fewer key files.*/}
                {/*                    </div>*/}
                {/*                )}*/}
                {/*            </div>*/}
                {/*        </div>*/}
                {/*        {projectAnalysis.issues.length > 0 && (*/}
                {/*            <div style={{*/}
                {/*                backgroundColor: '#fff',*/}
                {/*                borderRadius: '12px',*/}
                {/*                padding: '24px',*/}
                {/*                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',*/}
                {/*                marginBottom: '24px',*/}
                {/*                border: '1px solid #fed7aa'*/}
                {/*            }}>*/}
                {/*                <h3 style={{*/}
                {/*                    color: '#ea580c',*/}
                {/*                    marginBottom: '16px',*/}
                {/*                    fontSize: '18px',*/}
                {/*                    fontWeight: '600',*/}
                {/*                    display: 'flex',*/}
                {/*                    alignItems: 'center',*/}
                {/*                    gap: '8px'*/}
                {/*                }}>*/}
                {/*                    <Zap size={20}/>*/}
                {/*                    Improvement Opportunities*/}
                {/*                </h3>*/}
                {/*                <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>*/}
                {/*                    {projectAnalysis.issues.map((issue, index) => (*/}
                {/*                        <div key={index} style={{*/}
                {/*                            padding: '12px 16px',*/}
                {/*                            backgroundColor: '#fef3c7',*/}
                {/*                            borderRadius: '8px',*/}
                {/*                            border: '1px solid #fde68a',*/}
                {/*                            display: 'flex',*/}
                {/*                            alignItems: 'center',*/}
                {/*                            gap: '12px'*/}
                {/*                        }}>*/}
                {/*                            <div style={{*/}
                {/*                                width: '8px',*/}
                {/*                                height: '8px',*/}
                {/*                                backgroundColor: '#d97706',*/}
                {/*                                borderRadius: '50%'*/}
                {/*                            }}/>*/}
                {/*                            <span style={{color: '#92400e', fontSize: '14px'}}>{issue}</span>*/}
                {/*                        </div>*/}
                {/*                    ))}*/}
                {/*                </div>*/}
                {/*            </div>*/}
                {/*        )}*/}

                {/*        /!* AI Suggestions *!/*/}
                {/*        <div style={{*/}
                {/*            backgroundColor: '#fff',*/}
                {/*            borderRadius: '12px',*/}
                {/*            padding: '24px',*/}
                {/*            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',*/}
                {/*            border: '1px solid #e5d8c8'*/}
                {/*        }}>*/}
                {/*            <div style={{*/}
                {/*                display: 'flex',*/}
                {/*                justifyContent: 'space-between',*/}
                {/*                alignItems: 'center',*/}
                {/*                marginBottom: '20px'*/}
                {/*            }}>*/}
                {/*                <h3 style={{*/}
                {/*                    color: '#5C4033',*/}
                {/*                    fontSize: '18px',*/}
                {/*                    fontWeight: '600',*/}
                {/*                    display: 'flex',*/}
                {/*                    alignItems: 'center',*/}
                {/*                    gap: '8px',*/}
                {/*                    margin: 0*/}
                {/*                }}>*/}
                {/*                    <Wand2 size={20} style={{color: '#8B4513'}}/>*/}
                {/*                    AI Suggestions*/}
                {/*                    {isAIThinking && !applyProgress && (*/}
                {/*                        <div style={{*/}
                {/*                            width: '16px',*/}
                {/*                            height: '16px',*/}
                {/*                            border: '2px solid rgba(139, 69, 19, 0.3)',*/}
                {/*                            borderTopColor: '#8B4513',*/}
                {/*                            borderRadius: '50%',*/}
                {/*                            animation: 'spin 1s linear infinite'*/}
                {/*                        }}/>*/}
                {/*                    )}*/}
                {/*                </h3>*/}
                {/*                <div style={{display: 'flex', gap: '8px'}}>*/}
                {/*                    <button*/}
                {/*                        onClick={() => setShowFileSelector(true)}*/}
                {/*                        style={{*/}
                {/*                            padding: '8px 16px',*/}
                {/*                            backgroundColor: 'transparent',*/}
                {/*                            color: '#5C4033',*/}
                {/*                            border: '1px solid #5C4033',*/}
                {/*                            borderRadius: '6px',*/}
                {/*                            cursor: 'pointer',*/}
                {/*                            fontWeight: '500',*/}
                {/*                            fontSize: '14px',*/}
                {/*                            display: 'flex',*/}
                {/*                            alignItems: 'center',*/}
                {/*                            gap: '6px'*/}
                {/*                        }}*/}
                {/*                    >*/}
                {/*                        📁 Manage Files ({selectedFiles.size})*/}
                {/*                    </button>*/}
                {/*                    <button*/}
                {/*                        onClick={() => {*/}
                {/*                            console.log(`🧪 Testing: Generating suggestions with ${selectedFiles.size} selected files`);*/}
                {/*                            generateAISuggestions();*/}
                {/*                        }}*/}
                {/*                        disabled={isAIThinking}*/}
                {/*                        style={{*/}
                {/*                            padding: '8px 16px',*/}
                {/*                            backgroundColor: isAIThinking ? '#9B5523' : '#8B4513',*/}
                {/*                            color: '#FFF5EE',*/}
                {/*                            border: 'none',*/}
                {/*                            borderRadius: '6px',*/}
                {/*                            cursor: isAIThinking ? 'not-allowed' : 'pointer',*/}
                {/*                            fontWeight: '500',*/}
                {/*                            display: 'flex',*/}
                {/*                            alignItems: 'center',*/}
                {/*                            gap: '6px',*/}
                {/*                            fontSize: '14px'*/}
                {/*                        }}*/}
                {/*                    >*/}
                {/*                        {isAIThinking && !applyProgress ? (*/}
                {/*                            <>*/}
                {/*                                <div style={{*/}
                {/*                                    width: '14px',*/}
                {/*                                    height: '14px',*/}
                {/*                                    border: '2px solid rgba(255,255,255,0.3)',*/}
                {/*                                    borderTopColor: '#FFF5EE',*/}
                {/*                                    borderRadius: '50%',*/}
                {/*                                    animation: 'spin 1s linear infinite'*/}
                {/*                                }}/>*/}
                {/*                                Analyzing...*/}
                {/*                            </>*/}
                {/*                        ) : (*/}
                {/*                            <>*/}
                {/*                                <Plus size={14}/>*/}
                {/*                                Request More*/}
                {/*                            </>*/}
                {/*                        )}*/}
                {/*                    </button>*/}
                {/*                </div>*/}
                {/*            </div>*/}

                {/*            {suggestions.length === 0 && !isAIThinking && (*/}
                {/*                <div style={{*/}
                {/*                    textAlign: 'center',*/}
                {/*                    padding: '40px',*/}
                {/*                    color: '#8B4513',*/}
                {/*                    fontSize: '14px'*/}
                {/*                }}>*/}
                {/*                    AI suggestions will appear here after analysis...*/}
                {/*                </div>*/}
                {/*            )}*/}

                {/*            /!* Global Apply Progress Indicator *!/*/}
                {/*            {applyProgress && (*/}
                {/*                <div style={{*/}
                {/*                    padding: '16px',*/}
                {/*                    backgroundColor: '#f0f9ff',*/}
                {/*                    border: '1px solid #0ea5e9',*/}
                {/*                    borderRadius: '8px',*/}
                {/*                    marginBottom: '16px',*/}
                {/*                    display: 'flex',*/}
                {/*                    alignItems: 'center',*/}
                {/*                    gap: '12px'*/}
                {/*                }}>*/}
                {/*                    <div style={{*/}
                {/*                        width: '20px',*/}
                {/*                        height: '20px',*/}
                {/*                        border: '3px solid rgba(14, 165, 233, 0.3)',*/}
                {/*                        borderTopColor: '#0ea5e9',*/}
                {/*                        borderRadius: '50%',*/}
                {/*                        animation: 'spin 1s linear infinite'*/}
                {/*                    }}/>*/}
                {/*                    <div>*/}
                {/*                        <div style={{fontWeight: '600', color: '#0c4a6e', fontSize: '14px'}}>*/}
                {/*                            Applying AI Suggestion...*/}
                {/*                        </div>*/}
                {/*                        <div style={{fontSize: '12px', color: '#075985', marginTop: '2px'}}>*/}
                {/*                            {applyProgress.message} ({applyProgress.progress}%)*/}
                {/*                        </div>*/}
                {/*                    </div>*/}
                {/*                    <div style={{*/}
                {/*                        marginLeft: 'auto',*/}
                {/*                        fontSize: '14px',*/}
                {/*                        fontWeight: '600',*/}
                {/*                        color: '#0c4a6e'*/}
                {/*                    }}>*/}
                {/*                        {applyProgress.progress}%*/}
                {/*                    </div>*/}
                {/*                </div>*/}
                {/*            )}*/}

                {/*            <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>*/}
                {/*                {suggestions.map(suggestion => (*/}
                {/*                    <div key={suggestion.id} style={{*/}
                {/*                        padding: '20px',*/}
                {/*                        border: '1px solid #e5d8c8',*/}
                {/*                        borderRadius: '8px',*/}
                {/*                        backgroundColor: '#fdfcfb',*/}
                {/*                        transition: 'all 0.2s ease'*/}
                {/*                    }}>*/}
                {/*                        <div style={{*/}
                {/*                            display: 'flex',*/}
                {/*                            justifyContent: 'space-between',*/}
                {/*                            alignItems: 'start',*/}
                {/*                            marginBottom: '12px'*/}
                {/*                        }}>*/}
                {/*                            <div style={{flex: 1}}>*/}
                {/*                                <div style={{*/}
                {/*                                    display: 'flex',*/}
                {/*                                    alignItems: 'center',*/}
                {/*                                    gap: '12px',*/}
                {/*                                    marginBottom: '8px'*/}
                {/*                                }}>*/}
                {/*                                    <span style={{*/}
                {/*                                        padding: '3px 8px',*/}
                {/*                                        borderRadius: '12px',*/}
                {/*                                        fontSize: '11px',*/}
                {/*                                        fontWeight: '600',*/}
                {/*                                        textTransform: 'uppercase',*/}
                {/*                                        backgroundColor: suggestion.type === 'feature' ? '#dbeafe' :*/}
                {/*                                            suggestion.type === 'enhancement' ? '#dcfce7' :*/}
                {/*                                                suggestion.type === 'optimization' ? '#fef3c7' : '#fee2e2',*/}
                {/*                                        color: suggestion.type === 'feature' ? '#1e40af' :*/}
                {/*                                            suggestion.type === 'enhancement' ? '#16a34a' :*/}
                {/*                                                suggestion.type === 'optimization' ? '#d97706' : '#dc2626'*/}
                {/*                                    }}>*/}
                {/*                                        {suggestion.type}*/}
                {/*                                    </span>*/}
                {/*                                    <span style={{*/}
                {/*                                        padding: '3px 8px',*/}
                {/*                                        borderRadius: '12px',*/}
                {/*                                        fontSize: '11px',*/}
                {/*                                        fontWeight: '600',*/}
                {/*                                        backgroundColor: suggestion.impact === 'high' ? '#fee2e2' :*/}
                {/*                                            suggestion.impact === 'medium' ? '#fef3c7' : '#f0f9ff',*/}
                {/*                                        color: suggestion.impact === 'high' ? '#dc2626' :*/}
                {/*                                            suggestion.impact === 'medium' ? '#d97706' : '#2563eb'*/}
                {/*                                    }}>*/}
                {/*                                        {suggestion.impact} impact*/}
                {/*                                    </span>*/}
                {/*                                    <span style={{*/}
                {/*                                        padding: '3px 8px',*/}
                {/*                                        borderRadius: '12px',*/}
                {/*                                        fontSize: '11px',*/}
                {/*                                        fontWeight: '600',*/}
                {/*                                        backgroundColor: '#f3f4f6',*/}
                {/*                                        color: '#6b7280'*/}
                {/*                                    }}>*/}
                {/*                                        {suggestion.category}*/}
                {/*                                    </span>*/}
                {/*                                </div>*/}
                {/*                                <h4 style={{*/}
                {/*                                    fontSize: '16px',*/}
                {/*                                    fontWeight: '600',*/}
                {/*                                    color: '#5C4033',*/}
                {/*                                    margin: '0 0 8px 0'*/}
                {/*                                }}>*/}
                {/*                                    {suggestion.title}*/}
                {/*                                </h4>*/}
                {/*                                <p style={{*/}
                {/*                                    fontSize: '14px',*/}
                {/*                                    color: '#8B4513',*/}
                {/*                                    margin: '0 0 8px 0',*/}
                {/*                                    lineHeight: '1.5'*/}
                {/*                                }}>*/}
                {/*                                    {suggestion.description}*/}
                {/*                                </p>*/}
                {/*                                <div style={{*/}
                {/*                                    fontSize: '12px',*/}
                {/*                                    color: '#6b7280',*/}
                {/*                                    display: 'flex',*/}
                {/*                                    gap: '16px'*/}
                {/*                                }}>*/}
                {/*                                    <span>Estimated time: {suggestion.estimatedTime}</span>*/}
                {/*                                    <span>Effort: {suggestion.effort}</span>*/}
                {/*                                </div>*/}
                {/*                                {suggestion.businessValue && (*/}
                {/*                                    <div style={{*/}
                {/*                                        fontSize: '12px',*/}
                {/*                                        color: '#16a34a',*/}
                {/*                                        marginTop: '8px',*/}
                {/*                                        fontStyle: 'italic'*/}
                {/*                                    }}>*/}
                {/*                                        💡 {suggestion.businessValue}*/}
                {/*                                    </div>*/}
                {/*                                )}*/}
                {/*                            </div>*/}
                {/*                            <button*/}
                {/*                                onClick={() => applySuggestionWithAI(suggestion)}*/}
                {/*                                disabled={isAIThinking}*/}
                {/*                                style={{*/}
                {/*                                    padding: '8px 16px',*/}
                {/*                                    backgroundColor: isAIThinking ? '#9B5523' : '#5C4033',*/}
                {/*                                    color: '#FFF5EE',*/}
                {/*                                    border: 'none',*/}
                {/*                                    borderRadius: '6px',*/}
                {/*                                    cursor: isAIThinking ? 'not-allowed' : 'pointer',*/}
                {/*                                    fontWeight: '500',*/}
                {/*                                    fontSize: '14px',*/}
                {/*                                    marginLeft: '16px',*/}
                {/*                                    whiteSpace: 'nowrap',*/}
                {/*                                    display: 'flex',*/}
                {/*                                    alignItems: 'center',*/}
                {/*                                    gap: '8px',*/}
                {/*                                    minWidth: '100px'*/}
                {/*                                }}*/}
                {/*                            >*/}
                {/*                                {applyProgress ? (*/}
                {/*                                    <>*/}
                {/*                                        <div style={{*/}
                {/*                                            width: '14px',*/}
                {/*                                            height: '14px',*/}
                {/*                                            border: '2px solid rgba(255,255,255,0.3)',*/}
                {/*                                            borderTopColor: '#FFF5EE',*/}
                {/*                                            borderRadius: '50%',*/}
                {/*                                            animation: 'spin 1s linear infinite'*/}
                {/*                                        }}/>*/}
                {/*                                        {applyProgress.progress}%*/}
                {/*                                    </>*/}
                {/*                                ) : isAIThinking ? (*/}
                {/*                                    'Apply'*/}
                {/*                                ) : (*/}
                {/*                                    'Apply'*/}
                {/*                                )}*/}
                {/*                            </button>*/}
                {/*                        </div>*/}
                {/*                    </div>*/}
                {/*                ))}*/}
                {/*            </div>*/}
                {/*        </div>*/}
                {/*    </div>*/}
                {/*)}*/}

                {activeSection === 'chat' && (
                    <AIStudioChat
                        activeFile={activeFile || files[0]?.name || ''}
                        fileContent={fileContent || files[0]?.content || ''}
                        files={files}
                        project={project}
                        onUpdateContent={onUpdateContent}
                        onError={onError}
                        onSuccess={onSuccess}
                        systemCanisters={systemCanisters}
                        userPrincipal={userPrincipal}
                        onFileSystemChange={onFileSystemChange}
                        userCanisters={userCanisters} // 🆕 ADD this line
                    />
                )}

                {/*{activeSection === 'features' && projectAnalysis && (*/}
                {/*    <FeaturesAndFunctionsPanel*/}
                {/*        project={project}*/}
                {/*        files={files}*/}
                {/*        projectAnalysis={projectAnalysis}*/}
                {/*        systemCanisters={systemCanisters}*/}
                {/*        userCanisters={userCanisters}*/}
                {/*        userPrincipal={userPrincipal}*/}
                {/*        onError={onError}*/}
                {/*        onSuccess={onSuccess}*/}
                {/*        onProjectUpdate={onProjectUpdate}*/}
                {/*        onFileSystemChange={onFileSystemChange}*/}
                {/*        isAIThinking={isAIThinking}*/}
                {/*    />*/}
                {/*)}*/}

                {/*{activeSection === 'defi' && projectAnalysis && (*/}
                {/*    <DeFiLabPanel*/}
                {/*        project={project}*/}
                {/*        files={files}*/}
                {/*        projectAnalysis={projectAnalysis}*/}
                {/*        systemCanisters={systemCanisters}*/}
                {/*        userCanisters={userCanisters}*/}
                {/*        userPrincipal={userPrincipal}*/}
                {/*        onError={onError}*/}
                {/*        onSuccess={onSuccess}*/}
                {/*        onProjectUpdate={onProjectUpdate}*/}
                {/*        onFileSystemChange={onFileSystemChange}*/}
                {/*        isAIThinking={isAIThinking}*/}
                {/*    />*/}
                {/*)}*/}

                {/*{activeSection === 'users' && (*/}
                {/*    <UserExperiencePanel*/}
                {/*        project={project}*/}
                {/*        files={files}*/}
                {/*        projectAnalysis={projectAnalysis}*/}
                {/*        systemCanisters={systemCanisters}*/}
                {/*        userCanisters={userCanisters}*/}
                {/*        userPrincipal={userPrincipal}*/}
                {/*        onError={onError}*/}
                {/*        onSuccess={onSuccess}*/}
                {/*        onProjectUpdate={onProjectUpdate}*/}
                {/*        onFileSystemChange={onFileSystemChange}*/}
                {/*        isAIThinking={isAIThinking}*/}
                {/*    />*/}
                {/*)}*/}

                {/*{(activeSection === 'data') && (*/}
                {/*    <DataLogicPanel*/}
                {/*        project={project}*/}
                {/*        files={files}*/}
                {/*        projectAnalysis={projectAnalysis}*/}
                {/*        systemCanisters={systemCanisters}*/}
                {/*        userCanisters={userCanisters}*/}
                {/*        userPrincipal={userPrincipal}*/}
                {/*        onError={onError}*/}
                {/*        onSuccess={onSuccess}*/}
                {/*        onProjectUpdate={onProjectUpdate}*/}
                {/*        onFileSystemChange={onFileSystemChange}*/}
                {/*        isAIThinking={isAIThinking}*/}
                {/*    />*/}
                {/*)}*/}

                {/*{activeSection === 'design' && (*/}
                {/*    <DesignStylePanel*/}
                {/*        project={project}*/}
                {/*        files={files}*/}
                {/*        projectAnalysis={projectAnalysis}*/}
                {/*        systemCanisters={systemCanisters}*/}
                {/*        userCanisters={userCanisters}*/}
                {/*        userPrincipal={userPrincipal}*/}
                {/*        onError={onError}*/}
                {/*        onSuccess={onSuccess}*/}
                {/*        onProjectUpdate={onProjectUpdate}*/}
                {/*        onFileSystemChange={onFileSystemChange}*/}
                {/*        isAIThinking={isAIThinking}*/}
                {/*    />*/}
                {/*)}*/}

            </div>

            {/* File Selector Modal */}
            {showFileSelector && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: '#fff',
                        borderRadius: '12px',
                        padding: '24px',
                        maxWidth: '800px',
                        maxHeight: '80vh',
                        overflow: 'hidden',
                        margin: '20px',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                        border: '1px solid #e5d8c8',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '20px'
                        }}>
                            <h3 style={{color: '#5C4033', margin: 0, fontSize: '18px', fontWeight: '600'}}>
                                📁 Select Files for AI Analysis
                            </h3>
                            <button
                                onClick={() => setShowFileSelector(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    color: '#8B4513',
                                    padding: '0',
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                ×
                            </button>
                        </div>

                        <div style={{
                            backgroundColor: '#fef3c7',
                            padding: '12px',
                            borderRadius: '8px',
                            marginBottom: '16px',
                            border: '1px solid #fde68a'
                        }}>
                            <div style={{fontSize: '14px', color: '#92400e', marginBottom: '8px'}}>
                                💡 <strong>Tip:</strong> Select 5-8 key files for best results. Too many files can
                                overwhelm Claude.
                            </div>
                            <div style={{fontSize: '12px', color: '#92400e'}}>
                                Selected: {selectedFiles.size} files ({Array.from(selectedFiles).reduce((sum, name) => {
                                const file = files.find(f => f.name === name);
                                return sum + (file?.content?.length || 0);
                            }, 0).toLocaleString()} characters)
                            </div>
                        </div>

                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            marginBottom: '16px',
                            flexWrap: 'wrap'
                        }}>
                            <button
                                onClick={() => setSelectedFiles(new Set())}
                                style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#f3f4f6',
                                    color: '#374151',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                }}
                            >
                                Clear All
                            </button>
                            <button
                                onClick={() => {
                                    const backendFiles = files.filter(f => f.name.endsWith('.mo'));
                                    setSelectedFiles(new Set(backendFiles.map(f => f.name)));
                                }}
                                style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#f0fdf4',
                                    color: '#16a34a',
                                    border: '1px solid #16a34a',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                }}
                            >
                                All Backend (.mo)
                            </button>
                            <button
                                onClick={() => {
                                    const frontendFiles = files.filter(f => f.name.endsWith('.tsx') || f.name.endsWith('.ts'));
                                    setSelectedFiles(new Set(frontendFiles.map(f => f.name)));
                                }}
                                style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#fef3c7',
                                    color: '#d97706',
                                    border: '1px solid #d97706',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                }}
                            >
                                All Frontend (.tsx/.ts)
                            </button>
                            <button
                                onClick={() => {
                                    const mainFiles = files.filter(f =>
                                        f.name.includes('main') ||
                                        f.name.includes('index') ||
                                        f.name.includes('app') ||
                                        f.name.endsWith('.mo')
                                    );
                                    setSelectedFiles(new Set(mainFiles.map(f => f.name)));
                                }}
                                style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#ede9fe',
                                    color: '#7c3aed',
                                    border: '1px solid #7c3aed',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                }}
                            >
                                Smart Select (Recommended)
                            </button>
                        </div>

                        <div style={{
                            flex: 1,
                            overflow: 'auto',
                            border: '1px solid #e5d8c8',
                            borderRadius: '8px'
                        }}>
                            {files.map(file => {
                                const isSelected = selectedFiles.has(file.name);
                                const fileSize = file.content?.length || 0;
                                const fileSizeKB = (fileSize / 1024).toFixed(1);
                                const lines = file.content?.split('\n').length || 0;

                                return (
                                    <div key={file.name} style={{
                                        padding: '12px 16px',
                                        borderBottom: '1px solid #f3f4f6',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        backgroundColor: isSelected ? '#f0f9ff' : 'transparent',
                                        cursor: 'pointer'
                                    }}
                                         onClick={() => {
                                             const newSelected = new Set(selectedFiles);
                                             if (isSelected) {
                                                 newSelected.delete(file.name);
                                             } else {
                                                 newSelected.add(file.name);
                                             }
                                             setSelectedFiles(newSelected);
                                         }}>
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => {
                                            }} // Handled by div click
                                            style={{margin: 0}}
                                        />
                                        <div style={{flex: 1}}>
                                            <div style={{
                                                fontWeight: '500',
                                                color: '#5C4033',
                                                fontSize: '14px'
                                            }}>
                                                {file.name}
                                            </div>
                                            <div style={{
                                                fontSize: '12px',
                                                color: '#8B4513',
                                                marginTop: '2px'
                                            }}>
                                                {lines} lines • {fileSizeKB} KB
                                            </div>
                                        </div>
                                        <div style={{
                                            padding: '2px 6px',
                                            borderRadius: '4px',
                                            fontSize: '10px',
                                            fontWeight: '500',
                                            backgroundColor: file.name.endsWith('.mo') ? '#dcfce7' :
                                                file.name.endsWith('.tsx') || file.name.endsWith('.ts') ? '#fef3c7' :
                                                    file.name.endsWith('.css') ? '#fce7f3' : '#f3f4f6',
                                            color: file.name.endsWith('.mo') ? '#16a34a' :
                                                file.name.endsWith('.tsx') || file.name.endsWith('.ts') ? '#d97706' :
                                                    file.name.endsWith('.css') ? '#db2777' : '#6b7280'
                                        }}>
                                            {file.name.split('.').pop()?.toUpperCase()}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div style={{
                            marginTop: '20px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div style={{fontSize: '12px', color: '#8B4513'}}>
                                {selectedFiles.size} files selected
                            </div>
                            <div style={{display: 'flex', gap: '12px'}}>
                                <button
                                    onClick={() => setShowFileSelector(false)}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: 'transparent',
                                        color: '#5C4033',
                                        border: '1px solid #5C4033',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontWeight: '500'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        setShowFileSelector(false);
                                        if (pendingSuggestion) {
                                            applySuggestionWithAI(pendingSuggestion);
                                            setPendingSuggestion(null);
                                        }
                                    }}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: '#5C4033',
                                        color: '#FFF5EE',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontWeight: '500'
                                    }}
                                >
                                    {pendingSuggestion ? 'Apply with Selected Files' : 'Save Selection'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Information Modal */}
            {showInfoModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: '#fff',
                        borderRadius: '12px',
                        padding: '24px',
                        maxWidth: '600px',
                        maxHeight: '80vh',
                        overflow: 'auto',
                        margin: '20px',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                        border: '1px solid #e5d8c8'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '20px'
                        }}>
                            <h3 style={{color: '#5C4033', margin: 0, fontSize: '18px', fontWeight: '600'}}>
                                {modalContent.title}
                            </h3>
                            <button
                                onClick={() => setShowInfoModal(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    color: '#8B4513',
                                    padding: '0',
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                ×
                            </button>
                        </div>
                        <div style={{
                            whiteSpace: 'pre-line',
                            color: '#5C4033',
                            fontSize: '14px',
                            lineHeight: '1.6',
                            fontFamily: 'monospace',
                            backgroundColor: '#f8f9fa',
                            padding: '16px',
                            borderRadius: '8px',
                            border: '1px solid #e5d8c8'
                        }}>
                            {modalContent.content}
                        </div>
                        <div style={{marginTop: '20px', textAlign: 'right'}}>
                            <button
                                onClick={() => setShowInfoModal(false)}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#5C4033',
                                    color: '#FFF5EE',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontWeight: '500'
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* CSS for animations */}
            <style dangerouslySetInnerHTML={{
                __html: `
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                `
            }}/>
        </div>
    );
};

export default AIStudioView;